import axios from 'axios'
import { toast } from 'vue-sonner'
import {
  filterNull,
  getRequestTimestamp,
  xorEncrypt,
} from '@/utils/requestCrypto'

// 请求重试配置
const MAX_RETRY_COUNT = 3 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟时间（毫秒）

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  export interface AxiosRequestConfig {
    retry?: boolean
    retryCount?: number
    /** 不加密请求体（默认会加密） */
    skipEncrypt?: boolean
    /** 跳过全局错误处理（不 toast、不 requestLogout），业务失败仍 reject，由调用方处理 */
    passError?: boolean
  }
}

const api = axios.create({
  baseURL: import.meta.env.VITE_OPEN_PROXY ? '/proxy/' : import.meta.env.VITE_APP_API_BASEURL,
  timeout: 1000 * 60,
  responseType: 'json',
})

api.interceptors.request.use(
  (request) => {
    // 全局拦截请求发送前提交的参数
    const userStore = useUserStore()

    // 非登录接口且未登录时可由路由或业务层处理，这里照常带 token（无则空）
    if (request.params) {
      filterNull(request.params)
    }
    if (request.data && typeof request.data === 'object') {
      filterNull(request.data)
    }

    const { newTime, nonstr } = getRequestTimestamp()
    const time = Date.now()

    Object.assign(request.headers, {
      'Content-Type': 'application/json',
      'x-token': userStore.token ?? '',
      'opid': String(time),
      'timestamp': newTime,
      'nonstr': nonstr,
      'lang': 'zh-Hant',
    })
    console.warn('api request', request.url, request.data)
    // 有 body 且不跳过加密：整体包装为 { data: xorEncrypt({ operationID, ...data }) }
    if (request.data !== undefined && request.data !== null && !request.skipEncrypt) {
      request.data = {
        data: xorEncrypt({
          operationID: String(Date.now()),
          ...(typeof request.data === 'object' && request.data !== null ? request.data : {}),
        }),
      }
    }

    return request
  },
  (error) => {
    toast.error('Error', { description: error?.message ?? '请求配置错误' })
    return Promise.reject(error)
  },
)

// 处理错误信息的函数
function handleError(error: any) {
  if (error.status === 401) {
    useUserStore().requestLogout()
  }
  else {
    let message = error.message
    if (message === 'Network Error') {
      message = '后端网络故障'
    }
    else if (message.includes('timeout')) {
      message = '接口请求超时'
    }
    else if (message.includes('Request failed with status code')) {
      message = `接口${message.substr(message.length - 3)}异常`
    }
    toast.error('请求失败', {
      description: message,
    })
  }
  return Promise.reject(error)
}

api.interceptors.response.use(
  (response) => {
    /**
     * 全局拦截请求发送后返回的数据，如果数据有报错则在这做全局的错误提示
     * 假设返回数据格式为：{ status: 1, error: '', data: {} }
     * 规则是当 status 为 1 时表示请求成功，为 0 时表示接口需要登录或者登录状态失效，需要重新登录
     * 请求出错时 error 会返回错误信息
     */
    if (typeof response.data === 'object') {
      if (response.data.code === 0) {
        // 请求成功
        return Promise.resolve(response.data)
      }

      // 跳过全局错误处理（不 toast、不 requestLogout），但业务失败仍 reject，由调用方处理
      if (response.config.passError) {
        return Promise.reject(response.data)
      }

      // 登录状态失效，需要重新登录
      if (response.data.data?.reload || response.data.code === 520 || response.data.code === 524 || response.data.code === 525) {
        useUserStore().requestLogout()
      }

      return Promise.reject(response.data)
    }
    else {
      toast.error('请求失败', {
        description: '未知返回数据格式',
      })
      return Promise.reject(response.data)
    }
  },
  async (error) => {
    // 获取请求配置
    const config = error.config
    // 如果配置不存在或未启用重试，则直接处理错误
    if (!config || !config.retry) {
      return handleError(error)
    }
    // 设置重试次数
    config.retryCount = config.retryCount || 0
    // 判断是否超过重试次数
    if (config.retryCount >= MAX_RETRY_COUNT) {
      return handleError(error)
    }
    // 重试次数自增
    config.retryCount += 1
    // 延迟重试
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
    // 重新发起请求
    return api(config)
  },
)

export default api
